/**
 * GA4 conversion-event schema and firing guards.
 *
 * Pure and DOM-free on purpose: every function here decides *whether* an event should fire and
 * *what* params it carries, without touching `window`/`document`/`gtag` itself. Call sites pass
 * the result to `trackEvent` from `@/lib/analytics` (the one place that talks to gtag). Keeping
 * the decision logic separate is what makes duplicate-fire guards and param shapes testable
 * under the plain `node:test` runner this repo uses — there is no DOM in that environment.
 *
 * Every event here is deliberately free of PII: no email, name, booking guest details, event
 * titles, or other free-text/high-cardinality identifiers. Only small, stable enums and opaque
 * ids used purely for dedup (never sent as a GA4 param) cross this boundary.
 */

export type SignUpEvent = { name: "sign_up"; params: { method: "oauth" } };
export type OnboardingStepEvent = {
  name: "onboarding_step";
  params: { step: string; from: string; step_index: number };
};
export type EventTypeCreatedEvent = {
  name: "event_type_created";
  params: { event_type_kind: EventTypeKind };
};
export type BookingCompletedEvent = {
  name: "booking_completed";
  params: { host_kind: string };
};
/**
 * `transaction_id` is the checkout attempt's own id — a server-generated UUID that exists purely
 * to identify this transaction, never anything derived from the user (no email, no name, no
 * booking/account identifiers). `value` is deliberately absent: the backend has no reconciled
 * charged amount to stand behind (see CheckoutAttemptStatusDto's Javadoc on the API side) — only
 * currency is provider-verified. GA4 accepts a purchase event without `value`; its Monetization
 * reports simply will not attribute revenue to these events until a real reconciled amount exists
 * to report, which is the honest state of what this system currently knows.
 */
export type PurchaseEvent = {
  name: "purchase";
  params: { transaction_id: string; currency?: string; plan_tier: PlanTier };
};

export type EventTypeKind = "ONE_ON_ONE" | "GROUP" | "ROUND_ROBIN" | "COLLECTIVE";
export type PlanTier = "FREE" | "PROFESSIONAL" | "TEAM" | "ENTERPRISE" | "UNKNOWN";

const EVENT_TYPE_KINDS: readonly EventTypeKind[] = ["ONE_ON_ONE", "GROUP", "ROUND_ROBIN", "COLLECTIVE"];
/** Real, purchasable tiers only — UNKNOWN is the explicit missing-data fallback, never a real tier to match against. */
const PLAN_TIERS: readonly PlanTier[] = ["FREE", "PROFESSIONAL", "TEAM", "ENTERPRISE"];

/**
 * `sign_up` fires once, only when the backend has said outright that this OAuth callback
 * created a brand-new account (see the `newUser` redirect param set by
 * OAuth2AuthenticationSuccessHandler). There is no frontend heuristic here — a value that is
 * not exactly the marker this module expects is treated as "not a new signup" and the caller
 * gets `null`, never a guess.
 */
export function buildSignUpEvent(newUserMarker: string | null): SignUpEvent | null {
  if (newUserMarker !== "1") return null;
  return { name: "sign_up", params: { method: "oauth" } };
}

/** Unchanged shape — preserves the existing onboarding_step event exactly as it fires today. */
export function buildOnboardingStepEvent(input: {
  step: string;
  from: string;
  stepIndex: number;
}): OnboardingStepEvent {
  return {
    name: "onboarding_step",
    params: { step: input.step, from: input.from, step_index: input.stepIndex },
  };
}

function normalizeEventTypeKind(kind: string | null | undefined): EventTypeKind {
  return (EVENT_TYPE_KINDS as readonly string[]).includes(kind ?? "")
    ? (kind as EventTypeKind)
    : "ONE_ON_ONE";
}

/**
 * `event_type_created` fires once per successful `api.createEventType(...)` call — call sites
 * invoke this only after that request has already resolved, never on merely visiting the
 * success route. No dedup guard is needed here: unlike booking confirmation or checkout, there
 * is no route/remount path that can replay a create call without the user deliberately
 * submitting the form again.
 */
export function buildEventTypeCreatedEvent(kind: string | null | undefined): EventTypeCreatedEvent {
  return { name: "event_type_created", params: { event_type_kind: normalizeEventTypeKind(kind) } };
}

/**
 * `booking_completed` firing decision, kept separate from any React effect so the dedup rule is
 * testable on its own. `firedBookingIds` is the caller's per-mount ref set (mirrors the
 * `lifecycleLoggedRef` pattern already used in ConfirmedView.tsx) — a booking id already present
 * means this exact confirmation already reported, so the event must not fire again.
 */
export function shouldFireBookingCompleted(
  bookingId: string | null | undefined,
  firedBookingIds: ReadonlySet<string>,
): boolean {
  return Boolean(bookingId) && !firedBookingIds.has(bookingId as string);
}

export function buildBookingCompletedEvent(hostKind: string): BookingCompletedEvent {
  return { name: "booking_completed", params: { host_kind: hostKind } };
}

/**
 * A missing/unrecognized tier reports as UNKNOWN, never FREE. A successful paid purchase whose
 * plan_tier failed to persist (e.g. the plans catalogue query hadn't resolved yet at checkout
 * start) is a data-quality gap, not evidence the purchase was actually of the free tier — FREE
 * must only ever mean an actual, deliberate free-tier selection.
 */
function normalizePlanTier(tier: string | null | undefined): PlanTier {
  return (PLAN_TIERS as readonly string[]).includes(tier ?? "") ? (tier as PlanTier) : "UNKNOWN";
}

/**
 * `purchase` firing decision. `status` must be the backend's own reconciled checkout-attempt
 * status (`api.reconcileCheckoutAttempt`), which does a real provider read — never the payment
 * provider's redirect query params, which only prove a return happened, not that it succeeded.
 * `firedAttemptIds` guards against the poll loop, a remount, or a resumed-on-reload reconciliation
 * reporting the same attempt twice.
 */
export function shouldFirePurchase(
  status: string | null | undefined,
  attemptId: string | null | undefined,
  firedAttemptIds: ReadonlySet<string>,
): boolean {
  return status === "SUCCEEDED" && Boolean(attemptId) && !firedAttemptIds.has(attemptId as string);
}

export function buildPurchaseEvent(
  attemptId: string,
  currency: string | null | undefined,
  planTier: string | null | undefined,
): PurchaseEvent {
  return {
    name: "purchase",
    params: {
      transaction_id: attemptId,
      ...(currency ? { currency } : {}),
      plan_tier: normalizePlanTier(planTier),
    },
  };
}

/**
 * Whether a page load should resume backend reconciliation for a checkout attempt left in
 * localStorage from an earlier visit — the fix for reload/navigation during the original poll
 * silently abandoning a purchase that was genuinely about to (or already did) succeed.
 *
 * `hasReturnParam` is whether THIS load's URL carries the provider's own one-shot return signal
 * (?checkout=success, or Dodo's subscription_id/status shape) — when true, the caller already
 * knows to poll regardless of this function. This function answers the harder case: no return
 * param on this load (it was consumed by an earlier mount, or this is a plain reload), but a
 * stored attempt id still exists. Resuming is only correct within a bounded age — an attempt
 * abandoned days ago and never cleaned up must not be resurrected and polled forever.
 *
 * This never treats a stored attempt as evidence of anything by itself: it only decides whether
 * it's worth ASKING the backend again. The backend's reconcile status remains the only thing that
 * can make `purchase` fire (see shouldFirePurchase).
 */
export function shouldResumeCheckoutReconciliation(input: {
  hasReturnParam: boolean;
  storedAttemptId: string | null;
  storedAtMs: number | null;
  nowMs: number;
  maxAgeMs: number;
}): boolean {
  if (input.hasReturnParam) return false; // the normal, already-handled path — nothing to resume
  if (!input.storedAttemptId) return false;
  if (input.storedAtMs == null) return false; // no timestamp to bound the resume by — treat as stale, don't guess
  return input.nowMs - input.storedAtMs <= input.maxAgeMs;
}
